<template>
  <div class="m7-tune">
    <div class="m7-tune__toggle">
      <span class="m7-tune__toggle-label" :class="{ 'is-on': store.settings.muted }">{{ $t('metronome.off') }}</span>
      <v-switch
        :model-value="!store.settings.muted"
        color="primary"
        density="compact"
        hide-details
        inset
        :aria-label="$t('metronome.sound')"
        @update:model-value="store.update('muted', !$event)"
      />
      <span class="m7-tune__toggle-label" :class="{ 'is-on': !store.settings.muted }">{{ $t('metronome.on') }}</span>
    </div>

    <div class="m7-tune__sliders">
      <div v-for="band in bands" :key="band.key" class="m7-tune__band">
        <span class="m7-tune__value">{{ band.display }}</span>
        <v-slider
          :model-value="band.value"
          :min="band.min"
          :max="band.max"
          :step="band.step"
          direction="vertical"
          density="compact"
          color="primary"
          track-size="3"
          thumb-size="14"
          hide-details
          :aria-label="band.label"
          @update:model-value="band.set"
        />
        <span class="m7-tune__label">{{ band.label }}</span>
      </div>
    </div>

    <div class="m7-tune__actions">
      <v-btn variant="outlined" size="small" @click="reset">{{ $t('metronome.reset') }}</v-btn>
      <v-btn variant="outlined" size="small" prepend-icon="$play" @click="emit('preview')">
        {{ $t('metronome.preview') }}
      </v-btn>
    </div>
  </div>
</template>

<script setup>
const emit = defineEmits(['preview'])

const { t } = useI18n()
const store = useMetronomeStore()

function setEq(key, value) {
  store.update('eq', { ...store.settings.eq, [key]: value })
}

const bands = computed(() => [
  {
    key: 'volume',
    label: t('metronome.volume'),
    display: `${Math.round(store.settings.volume * 100)}`,
    value: store.settings.volume,
    min: 0,
    max: 1,
    step: 0.01,
    set: (value) => store.update('volume', value),
  },
  {
    key: 'pitch',
    label: t('metronome.pitch'),
    display: formatSigned(store.settings.pitch),
    value: store.settings.pitch,
    min: -12,
    max: 12,
    step: 0.5,
    set: (value) => store.update('pitch', value),
  },
  ...['low', 'mid', 'high'].map((key) => ({
    key,
    label: t(`metronome.eq.${key}`),
    display: formatSigned(store.settings.eq[key]),
    value: store.settings.eq[key],
    min: -20,
    max: 20,
    step: 1,
    set: (value) => setEq(key, value),
  })),
])

function formatSigned(value) {
  const rounded = Math.round(value * 10) / 10
  return rounded > 0 ? `+${rounded}` : `${rounded}`
}

function reset() {
  store.update('pitch', 0)
  store.update('volume', 0.8)
  store.update('eq', { low: 0, mid: 0, high: 0 })
}
</script>

<style scoped>
.m7-tune {
  text-align: center;
}

.m7-tune__toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 0.25rem;
}

.m7-tune__toggle-label {
  font-size: 0.62rem;
  letter-spacing: 0.16em;
  color: var(--m7-muted);
  transition: color 0.15s ease;
}

.m7-tune__toggle-label.is-on {
  color: var(--m7-accent);
}

.m7-tune__sliders {
  display: flex;
  justify-content: center;
  gap: 0.25rem;
  height: 190px;
}

.m7-tune__band {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  /* Without this the band refuses to shrink below the slider's intrinsic size. */
  min-height: 0;
}

/*
 * Vuetify hard-codes `min-height: 300px` on a vertical slider's control
 * (VSlider.css), which overflows this fixed-height row and pushes the band
 * labels out of the panel. Let the slider take exactly the height it is given.
 */
.m7-tune__band :deep(.v-slider.v-input--vertical) {
  flex: 1 1 auto;
  min-height: 0;
  margin-top: 4px;
  margin-bottom: 4px;
}

.m7-tune__band :deep(.v-slider.v-input--vertical > .v-input__control) {
  min-height: 0;
}

.m7-tune__value {
  font-size: 0.62rem;
  color: var(--m7-accent);
  letter-spacing: 0.04em;
}

.m7-tune__label {
  font-size: 0.58rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--m7-muted);
}

.m7-tune__actions {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
}
</style>
